import { Period } from '@lyra/core/api/types/public.get_spot_feed_history_candles'
import { fetchSpotFeedHistoryCandlesData } from '@lyra/web/utils/client/fetchSpotFeedHistoryCandlesData'
import { Bar, IBasicDataFeed, LibrarySymbolInfo, ResolutionString } from 'tradingview'

import { Market } from '../constants/markets'
import { Collateral, CurrencyId } from '../constants/tokens'

const configurationData = {
  supported_resolutions: ['15', '60', '240', '480', '1D', '1W'] as ResolutionString[],
  exchanges: [{ value: 'Derive', name: 'Derive', desc: 'Derive' }],
  symbols_types: [
    { name: 'ETH', value: 'ETH' },
    { name: 'BTC', value: 'BTC' },
  ],
}

function resolutionToPeriod(resolution: string): Period {
  switch (resolution) {
    case '15':
      return 900
    case '60':
      return 3600
    case '240':
      return 14400
    case '480':
      return 28800
    case '1D':
      return 86400
    case '1W':
      return 604800
    default:
      return 900
  }
}

function countDecimalsForSignificantFigures(num: number, significantFigures: number): number {
  if (!Number.isFinite(num) || num === 0) {
    return 0 // Handle edge cases like infinity, NaN, or zero
  }

  const absoluteValue = Math.abs(num)
  const decimalCount = significantFigures - Math.floor(Math.log10(absoluteValue)) - 1

  return decimalCount > 0 ? decimalCount : 0
}

export const createTradingViewDataFeed = (
  currencyId: CurrencyId,
  getCurrencies: () => Record<CurrencyId, Market | Collateral>
): IBasicDataFeed => {
  const { name, spotPrice } = getCurrencies()[currencyId]

  const estTickDps = countDecimalsForSignificantFigures(spotPrice, 5)

  const symbolItem = {
    symbol: currencyId,
    full_name: name,
    description: name,
    exchange: 'Derive',
    type: 'crypto',
  }

  const symbolInfo: LibrarySymbolInfo = {
    name: symbolItem.symbol,
    description: symbolItem.description,
    type: symbolItem.type,
    format: 'price',
    session: '24x7',
    timezone: 'Etc/UTC',
    exchange: symbolItem.exchange,
    listed_exchange: symbolItem.exchange,
    minmov: 1,
    pricescale: Math.pow(10, estTickDps),
    has_intraday: true,
    visible_plots_set: 'ohlc',
    supported_resolutions: configurationData.supported_resolutions,
  }

  let lastDailyBar: Bar
  let updateInterval: NodeJS.Timeout | null

  return {
    onReady: (callback: (config: typeof configurationData) => void) => {
      setTimeout(() => callback(configurationData), 0)
    },
    searchSymbols: (_0, _1, _2, onResultReadyCallback) => {
      onResultReadyCallback([symbolItem])
    },
    resolveSymbol: (_0, onSymbolResolvedCallback) => {
      setTimeout(() => onSymbolResolvedCallback(symbolInfo), 0)
    },
    getBars: async (symbolInfo, resolution, periodParams, onHistoryCallback, onErrorCallback) => {
      try {
        const { from, to } = periodParams
        const period = resolutionToPeriod(resolution)

        const rawData = await fetchSpotFeedHistoryCandlesData(symbolInfo.name, from, to, period)

        if (!rawData || rawData.length === 0) {
          onHistoryCallback([], { noData: true })
          return
        }

        const sortedData = rawData.sort((a, b) => a.timestamp - b.timestamp)

        const seenTimestamps = new Set<number>()
        const dedupedData = sortedData.filter((candle) => {
          if (seenTimestamps.has(candle.timestamp)) {
            return false
          } else {
            seenTimestamps.add(candle.timestamp)
            return true
          }
        })

        const data: Bar[] = dedupedData.map((candle) => ({
          time: candle.timestamp * 1000,
          low: candle.low,
          high: candle.high,
          open: candle.open,
          close: candle.close,
        }))

        lastDailyBar = data[data.length - 1]

        onHistoryCallback(data, { noData: false })
      } catch (error) {
        onErrorCallback('Error getting bars')
      }
    },
    subscribeBars: (_0, resolution, onRealtimeCallback) => {
      if (updateInterval) {
        clearInterval(updateInterval)
      }
      const period = resolutionToPeriod(resolution)

      updateInterval = setInterval(() => {
        const now = Date.now()
        const newBarTime = Math.max(lastDailyBar.time, now)

        const periodMs = period * 1000
        const currentPeriodStart = Math.floor(now / periodMs) * periodMs
        const spotPrice = getCurrencies()[currencyId].spotPrice

        if (!lastDailyBar || currentPeriodStart > lastDailyBar.time) {
          // Create a new candle
          lastDailyBar = {
            time: currentPeriodStart,
            open: spotPrice,
            high: spotPrice,
            low: spotPrice,
            close: spotPrice,
          }
        } else {
          // Update the existing candle
          lastDailyBar = {
            ...lastDailyBar,
            time: newBarTime,
            high: Math.max(lastDailyBar.high, spotPrice),
            low: Math.min(lastDailyBar.low, spotPrice),
            close: spotPrice,
          }
        }

        onRealtimeCallback(lastDailyBar)
      }, 1000)
    },

    unsubscribeBars: () => {
      if (updateInterval) {
        clearInterval(updateInterval)
        updateInterval = null
      }
    },
  }
}
